import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCcw } from 'lucide-react';

export default function RefundPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/welcome')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <RefreshCcw className="w-8 h-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-white">Refund Policy</h1>
        </div>
        
        <p className="text-slate-400 mb-8">Last updated: February 2026</p>

        <div className="space-y-8 text-slate-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Free Trial</h2>
            <p>We offer a 7-day free trial with full access to all features. No credit card is required to start your trial. This allows you to thoroughly evaluate our platform before making any payment.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Subscription Refunds</h2>
            
            <h3 className="text-lg font-medium text-teal-400 mb-2">2.1 Monthly Subscriptions</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Refund requests within 7 days of payment: Full refund</li>
              <li>Refund requests after 7 days: No refund (cancel for next billing cycle)</li>
            </ul>

            <h3 className="text-lg font-medium text-teal-400 mb-2 mt-4">2.2 Annual Subscriptions</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Refund requests within 7 days of payment: Full refund</li>
              <li>Refund requests within 30 days: Prorated refund minus one month</li>
              <li>Refund requests after 30 days: No refund (subscription remains active)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. How to Request a Refund</h2>
            <div className="bg-slate-900 p-6 rounded-lg">
              <p className="mb-4">To request a refund, please:</p>
              <ol className="list-decimal list-inside space-y-2">
                <li>Email us at <span className="text-teal-400">billing@wellnesttechnologies.com</span></li>
                <li>Include your registered email address</li>
                <li>Provide your company name</li>
                <li>State the reason for your refund request</li>
              </ol>
              <p className="mt-4 text-slate-400">We process refund requests within 5-7 business days.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Non-Refundable Items</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Setup or onboarding fees (if applicable)</li>
              <li>Custom development or integration work</li>
              <li>Third-party API costs (WhatsApp, SMS) already incurred</li>
              <li>Accounts terminated for Terms of Service violations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Plan Downgrades</h2>
            <p>If you downgrade to a lower-tier plan:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>The change takes effect at the next billing cycle</li>
              <li>No prorated refunds for the current period</li>
              <li>You retain access to current plan features until the cycle ends</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Cancellation</h2>
            <p>You can cancel your subscription at any time from your account settings:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Your service continues until the end of the current billing period</li>
              <li>You can export your data before cancellation</li>
              <li>Data is retained for 30 days after cancellation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Exceptional Circumstances</h2>
            <p>We may consider refunds outside this policy for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Extended service outages (more than 24 hours)</li>
              <li>Critical bugs preventing core functionality</li>
              <li>Billing errors on our part</li>
            </ul>
            <p className="mt-2 text-slate-400">Contact us to discuss your specific situation.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Refund Method</h2>
            <p>Refunds are processed to the original payment method:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Credit/Debit Card: 5-10 business days</li>
              <li>UPI/Net Banking: 3-5 business days</li>
              <li>Bank Transfer: 7-10 business days</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Contact Us</h2>
            <div className="mt-2 p-4 bg-slate-900 rounded-lg">
              <p><strong>Billing Support:</strong> billing@wellnesttechnologies.com</p>
              <p><strong>Phone:</strong> +91 8800977146</p>
              <p><strong>Hours:</strong> Monday - Saturday, 10 AM - 6 PM IST</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
